import { useDrag } from "@use-gesture/react";
import React from "react";
import { useContent } from "../../hooks";
import { impactLight } from "../../utils/haptics";
import { LanguageSwitcher } from "../ui";
import { MobileNavigationItem } from "./MobileNavigationItem";
import { NavItem } from "./NavItem";

export type MobileNavigationProps = {
    isOpen: boolean;
    onClose: () => void;
    items: NavItem[];
    language?: string;
    ctaLabel?: string;
    ctaHref?: string;
};

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose, items }) => {
    const { common } = useContent();
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [dragOffset, setDragOffset] = React.useState(0);
    // Swipe-to-dismiss gesture handler
    const bind = useDrag(
        ({
            movement: [mx],
            down,
            velocity: [vx],
        }: {
            movement: [number, number];
            down: boolean;
            velocity: [number, number];
        }) => {
            // Only allow dragging to the right (positive mx)
            const offset = Math.max(0, mx);

            if (down) {
                // Update position while dragging
                setDragOffset(offset);
            } else {
                // Check if threshold met: 150px or velocity > 0.5
                if (offset > 150 || vx > 0.5) {
                    // Trigger haptic feedback and close
                    impactLight();
                    onClose();
                }
                // Reset offset (will animate back via CSS transition or close completely)
                setDragOffset(0);
            }
        },
        {
            axis: "x",
            filterTaps: true,
            bounds: { left: 0 },
        }
    );

    // Lock body scroll when menu is open
    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            setDragOffset(0); // Reset drag offset when menu closes
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    // Handle ESC key to close menu
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    // Focus trap
    React.useEffect(() => {
        if (!isOpen || !menuRef.current) {
            return;
        }

        const focusableElements = menuRef.current.querySelectorAll<HTMLElement>(
            "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const handleTabKey = (e: KeyboardEvent) => {
            if (e.key !== "Tab") {
                return;
            }

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    lastElement?.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastElement) {
                    firstElement?.focus();
                    e.preventDefault();
                }
            }
        };

        document.addEventListener("keydown", handleTabKey);
        firstElement?.focus();

        return () => document.removeEventListener("keydown", handleTabKey);
    }, [isOpen]);

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/50 z-[800] transition-opacity duration-300 ${
                    isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Menu Panel */}
            <div
                {...bind()}
                ref={menuRef}
                className={`fixed inset-0 z-[1000] bg-primary-dark transform ${
                    dragOffset === 0 ? "transition-transform duration-300 ease-out" : ""
                } ${isOpen ? "translate-x-0" : "translate-x-full"}`}
                style={{
                    transform: dragOffset > 0 ? `translateX(${dragOffset}px)` : undefined,
                    touchAction: "pan-y", // Allow vertical scroll, prevent horizontal browser navigation
                }}
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation menu"
                data-testid="mobile-menu-panel"
            >
                <div className="flex flex-col h-full">
                    {/* Header with logo and close button */}
                    <div className="flex items-center justify-end p-6 border-b border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            // eslint-disable-next-line max-len
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                            aria-label={common?.navigation?.closeMenu}
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        <nav>
                            {items?.map((item, index) => (
                                <MobileNavigationItem key={index} item={item} onClose={onClose} />
                            ))}
                            {/* Language selector as last navigation item */}
                            <div className="w-full">
                                <LanguageSwitcher variant="dark" listMode={true} />
                            </div>
                        </nav>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MobileNavigation;
